import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { GeneralSettingsType } from '@type/general-settings.type';

import { AuthenticationService } from '@services/api/authentication.service';
import { TokenService } from '@services/helper/token.service';

import {
  GENERAL_SETTINGS_DEFAULTS,
  GENERAL_SETTINGS_OPTION_KEY,
} from '@models/general-settings.model';

/**
 * Snapshot of the live settings for callers that cannot inject a service —
 * the exported column formatters in `core/models/*-columns.model.ts` are plain
 * module-level functions consumed by table configs, not Angular constructs.
 *
 * Kept in step by the service constructor's effect. It is deliberately NOT the
 * source of truth: anything that can inject should read the signals instead.
 */
let liveSettings: GeneralSettingsType = { ...GENERAL_SETTINGS_DEFAULTS };

/** Current settings for non-injectable callers. Never mutate the result. */
export function currentGeneralSettings(): GeneralSettingsType {
  return liveSettings;
}

/**
 * Formats a date with the user's configured zone and date format. Shared by the
 * `appDate` pipe and the module-level table formatters so one setting governs
 * every surface.
 */
export function formatAppDate(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const { timezone, date_format } = liveSettings;
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    day: '2-digit',
    month: date_format === 'dd MMM yyyy' ? 'short' : '2-digit',
    year: 'numeric',
  }).formatToParts(date);

  const lookup = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  const day = lookup('day');
  const month = lookup('month');
  const year = lookup('year');

  switch (date_format) {
    case 'dd/MM/yyyy':
      return `${day}/${month}/${year}`;
    case 'MM/dd/yyyy':
      return `${month}/${day}/${year}`;
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`;
    default:
      return `${day} ${month} ${year}`;
  }
}

/** Formats a time with the user's configured zone and 12/24-hour preference. */
export function formatAppTime(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-AU', {
    timeZone: liveSettings.timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: liveSettings.time_format === '12h',
  }).format(date);
}

/**
 * Settings › General.
 *
 * Persists to the existing per-user `/me/options` blob (`users.user_options`)
 * under one key, rather than introducing a settings table — the endpoint
 * already merges unrelated keys server-side, so writing here cannot disturb the
 * saleboard zoom or dashboard order that share it.
 *
 * Hydration mirrors `SaleboardZoomService`: this is a root singleton, so it
 * tracks the auth token and re-reads on a logout → login within one session,
 * otherwise the next user inherits the previous one's settings.
 */
@Injectable({ providedIn: 'root' })
export class GeneralSettingsService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly tokenService = inject(TokenService);

  private hydrated = false;
  /** `undefined` = not yet observed, distinct from `null` = signed out. */
  private hydratedForToken: string | null | undefined = undefined;

  /**
   * The options payload that was on screen when the signed-in user changed.
   *
   * TanStack keeps serving the previous data for a query key until the refetch
   * lands, so without this the service would immediately re-hydrate the NEW
   * user from the OLD user's settings and only correct itself a moment later.
   * Holding the stale reference and refusing to hydrate until a different one
   * arrives keeps a user switch on the defaults until their own options load.
   */
  private staleOptionsRef: unknown = undefined;

  private readonly state = signal<GeneralSettingsType>({ ...GENERAL_SETTINGS_DEFAULTS });

  /** The whole settings object. */
  public readonly settings = computed<GeneralSettingsType>(() => this.state());

  public readonly timezone = computed<string>(() => this.state().timezone);
  public readonly dateFormat = computed(() => this.state().date_format);
  public readonly timeFormat = computed(() => this.state().time_format);
  public readonly sessionTimeoutMinutes = computed<number>(
    () => this.state().session_timeout_minutes,
  );
  /** Per-file upload ceiling in MB, for `app-file-upload`'s `maxFileSizeMB`. */
  public readonly maxUploadMb = computed<number>(() => this.state().max_upload_mb);

  /* computed, not a frozen boolean: this root singleton may first be
     constructed while the stored token is expired (returning after days away),
     and a baked-in `false` would keep the query disabled after re-login. */
  private readonly optionsQuery = this.authenticationService.getAuthenticatedUserOptionsQuery({
    enabled: computed(() => this.tokenService.isTokenValid()),
    refetchOnWindowFocus: false,
    select: (response: any) => {
      const { status, data } = response;
      return status ? data || {} : {};
    },
  });

  private readonly updateOptionsMutation =
    this.authenticationService.updateAuthenticatedUserOptionsMutation();

  constructor() {
    effect(() => {
      const token = this.tokenService.token();
      const firstRun = this.hydratedForToken === undefined;

      const options = this.optionsQuery.data() as Record<string, unknown> | undefined;

      if (!firstRun && token !== this.hydratedForToken) {
        this.hydrated = false;
        this.staleOptionsRef = options;
        this.state.set({ ...GENERAL_SETTINGS_DEFAULTS });
      }
      this.hydratedForToken = token;

      if (!token) return;
      if (this.hydrated || !options) return;
      /* Still the previous user's payload — wait for their own to arrive. */
      if (options === this.staleOptionsRef) return;

      this.hydrated = true;
      this.state.set(this.normalize(options[GENERAL_SETTINGS_OPTION_KEY]));
    });

    /* Mirror into the module-level snapshot the plain formatters read. */
    effect(() => {
      liveSettings = this.state();
    });
  }

  /**
   * Persists a complete settings object and applies it immediately. Returns the
   * mutation promise so the caller can surface success or failure.
   */
  public save(next: GeneralSettingsType): Promise<unknown> {
    const previous = this.state();
    this.state.set(next);

    return this.updateOptionsMutation
      .mutateAsync({ [GENERAL_SETTINGS_OPTION_KEY]: next })
      .catch((error: unknown) => {
        this.state.set(previous);
        throw error;
      });
  }

  /**
   * Coerces a stored blob back into a full settings object, falling back to the
   * default for any key that is missing or not one of the allowed values — the
   * blob is free-form JSON, so it can hold anything an older build wrote.
   */
  private normalize(raw: unknown): GeneralSettingsType {
    const source = (raw ?? {}) as Partial<Record<keyof GeneralSettingsType, unknown>>;
    const defaults = GENERAL_SETTINGS_DEFAULTS;

    const pick = <K extends keyof GeneralSettingsType>(
      key: K,
      allowed: ReadonlyArray<GeneralSettingsType[K]>,
    ): GeneralSettingsType[K] => {
      const value = source[key] as GeneralSettingsType[K];
      return allowed.includes(value) ? value : defaults[key];
    };

    const timezone = typeof source.timezone === 'string' ? source.timezone : defaults.timezone;
    const timeout = Number(source.session_timeout_minutes);
    const upload = Number(source.max_upload_mb);

    return {
      timezone,
      date_format: pick('date_format', ['dd MMM yyyy', 'dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd']),
      time_format: pick('time_format', ['12h', '24h']),
      session_timeout_minutes:
        Number.isFinite(timeout) && timeout >= 0 ? timeout : defaults.session_timeout_minutes,
      max_upload_mb: Number.isFinite(upload) && upload > 0 ? upload : defaults.max_upload_mb,
    };
  }
}
